// ============================================================================
// LIT-CASE — Assignments section
// ============================================================================
// Lists every case × role × user assignment (open + history).
// Open assignments displayed first; history shows below in a collapsible
// summary line. AGENT role is highlighted as the primary; secondary roles
// (CASE_OWNER, PARALEGAL, FIELD_STAFF, DOCUMENTATION_STAFF) appear in the
// catalogue's display_order.
//
// Source: GET /api/v1/CaseAssignment/by-case/{caseId}.

import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useCaseAssignments } from 'hooks/api/useCaseAssignments';
import type { CaseAssignment } from 'api/types/caseAssignment.types';
import type { LitigationCase } from 'api/types/litigationCase.types';

interface Props {
  kase: LitigationCase;
  onMutated: () => void;
}

const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return s; }
};

const CaseAssignmentsSection: React.FC<Props> = ({ kase }) => {
  const { data: assignments, loading, error } = useCaseAssignments(kase.id, false);
  const [showHistory, setShowHistory] = useState(false);

  const { open, history } = useMemo(() => {
    const o: CaseAssignment[] = [];
    const h: CaseAssignment[] = [];
    for (const a of assignments) (a.isOpen ? o : h).push(a);
    return { open: o, history: h };
  }, [assignments]);

  if (loading && assignments.length === 0) {
    return <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading assignments…</div>;
  }
  if (error) {
    return <div style={{ padding: '1.5rem', color: '#dc2626' }}>Failed to load: {error.message}</div>;
  }

  return (
    <div style={{ padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.85rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Assignments ({open.length})</h3>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
          Role-aware user assignments. AGENT drives the case TAT clock.
        </span>
      </div>

      {/* Open assignments */}
      {open.length === 0 ? (
        <div style={{
          padding: '2rem', textAlign: 'center', color: 'var(--text-muted)',
          border: '1px dashed var(--border-color)', borderRadius: 6,
        }}>
          <IconSystem.user size={28} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '0.9rem' }}>No open assignments yet.</div>
          <div style={{ fontSize: '0.78rem', marginTop: '0.4rem' }}>
            The case AGENT is set automatically on claim.
          </div>
        </div>
      ) : (
        <div style={{
          border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
              <tr>
                <th style={th('170px')}>Role</th>
                <th style={th()}>User</th>
                <th style={th('170px')}>Assigned</th>
              </tr>
            </thead>
            <tbody>
              {open.map(a => <AssignmentRow key={a.assignmentId} a={a} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* History toggle */}
      {history.length > 0 && (
        <div style={{ marginTop: '0.85rem' }}>
          <button
            type="button"
            onClick={() => setShowHistory(v => !v)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary)', fontSize: '0.8rem',
              padding: '0.25rem 0', display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            }}
          >
            {showHistory ? <IconSystem.chevronDown size={14} /> : <IconSystem.chevronRight size={14} />}
            History ({history.length})
          </button>
          {showHistory && (
            <div style={{
              marginTop: '0.4rem',
              border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead style={{ background: 'var(--bg-secondary)' }}>
                  <tr>
                    <th style={th('170px')}>Role</th>
                    <th style={th()}>User</th>
                    <th style={th('160px')}>Assigned</th>
                    <th style={th('160px')}>Unassigned</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(a => (
                    <tr key={a.assignmentId} style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td style={td()}>{a.roleName}</td>
                      <td style={td()}>{a.userName ?? a.userId.slice(0, 8)}</td>
                      <td style={td()}>{fmtDate(a.assignedAt)}</td>
                      <td style={td()}>{fmtDate(a.unassignedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AssignmentRow: React.FC<{ a: CaseAssignment }> = ({ a }) => (
  <tr style={{ borderTop: '1px solid var(--border-color)' }}>
    <td style={td()}>
      <span style={{
        fontSize: '0.78rem', fontWeight: 600,
        color: a.isPrimaryAgent ? '#1e40af' : 'var(--text-primary)',
      }}>
        {a.roleName}
        {a.isPrimaryAgent && (
          <span style={{
            marginLeft: '0.4rem', fontSize: '0.66rem', padding: '1px 6px',
            borderRadius: 4, background: '#dbeafe', color: '#1e40af',
          }}>
            primary
          </span>
        )}
      </span>
    </td>
    <td style={td()}>{a.userName ?? <code style={{ fontSize: '0.75rem' }}>{a.userId.slice(0, 8)}</code>}</td>
    <td style={td()}>{fmtDate(a.assignedAt)}</td>
  </tr>
);

const th = (width?: string): React.CSSProperties => ({
  padding: '0.5rem 0.625rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.78rem',
  borderBottom: '2px solid var(--border-color)',
  whiteSpace: 'nowrap',
  width,
});

const td = (): React.CSSProperties => ({
  padding: '0.45rem 0.625rem',
  verticalAlign: 'middle',
});

export default CaseAssignmentsSection;
